// components/ImagePeek.jsx
// ─────────────────────────────────────────────────────────────────────────────
// See a thumbnail properly without leaving the page.
//
// ASKED FOR 27 Sep 2026 on the cart: the line-item thumb is 56px, which is
// enough to know there IS a picture and not enough to confirm you are buying
// the right water jug.
//
// TWO CONTROLS, on purpose. Hover gives a PREVIEW — a floating panel, no
// backdrop, nothing to dismiss, gone the moment you move away. Click gives the
// MODAL — backdrop, Esc, a proper look. A modal that opened on hover would
// have to be dismissed every time the pointer crossed a row on its way
// somewhere else, which on a list is most of the time.
//
// THE PICTURES ARE PROTECTED. Vendor photography on this client carries the
// ImageGuard rules, and the enlarged views are exactly where a picture is
// worth taking, so both render through `ProtectedImage` rather than a bare
// <img> — its docstring names lightboxes and zoom stages as its reason to
// exist. A hover preview built with a plain <img> would put the largest,
// cleanest copy of every vendor photo one right-click from being saved.
//
// A PORTAL, because a preview anchored inside a row is clipped by the row.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import ProtectedImage from "./ProtectedImage";
import { optimizedImage } from "../utils/cloudinaryImage";

const PREVIEW = 300;
const GAP = 12;
const OPEN_DELAY_MS = 140;

/** Above the anchor when there is room, below when there is not, clamped
 *  inside the viewport. Exported so it can be tested without a browser. */
export const placePreview = (rect, viewport, size = PREVIEW, gap = GAP) => {
  if (!rect) return null;
  const above = rect.top - gap - size;
  const below = rect.bottom + gap;
  const top = above >= 8 ? above : Math.min(below, viewport.height - size - 8);
  const centred = rect.left + rect.width / 2 - size / 2;
  const left = Math.max(8, Math.min(centred, viewport.width - size - 8));
  return { top: Math.max(8, top), left };
};

const Panel = styled.div`
  position: fixed;
  z-index: 4000;
  width: ${PREVIEW}px;
  height: ${PREVIEW}px;
  background: #fff;
  border: 1px solid #eef1f2;
  border-radius: 12px;
  box-shadow: 0 14px 44px rgba(20, 40, 50, 0.22);
  padding: 8px;
  pointer-events: none; /* must never steal the hover that opened it */
  animation: peekIn 120ms ease-out;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @keyframes peekIn {
    from { opacity: 0; transform: translateY(4px) scale(0.98); }
    to   { opacity: 1; transform: none; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  /* No hover on a touch screen, so the panel would only ever flash on tap. */
  @media (hover: none) {
    display: none;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 4001;
  background: rgba(15, 30, 36, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Frame = styled.div`
  position: relative;
  background: #fff;
  border-radius: 14px;
  padding: 12px;
  max-width: min(720px, 92vw);
  max-height: 86vh;

  img {
    display: block;
    max-width: 100%;
    max-height: calc(86vh - 72px);
    object-fit: contain;
  }

  .cap {
    margin-top: 10px;
    font-size: 13.5px;
    font-weight: 600;
    color: #2c3a40;
    text-align: center;
  }
`;

const Close = styled.button`
  position: absolute;
  top: -14px;
  right: -14px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 0;
  background: #fff;
  color: #2c3a40;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);

  &:focus-visible {
    outline: 3px solid #7cb342;
    outline-offset: 2px;
  }
`;

export const HoverPreview = ({ src, rect, alt = "" }) => {
  if (!src || !rect || typeof document === "undefined") return null;
  const pos = placePreview(rect, {
    width: window.innerWidth,
    height: window.innerHeight,
  });
  if (!pos) return null;

  return ReactDOM.createPortal(
    <Panel style={{ top: pos.top, left: pos.left }} aria-hidden="true">
      <ProtectedImage src={optimizedImage(src, "card")} alt={alt} />
    </Panel>,
    document.body
  );
};

export const ImageModal = ({ src, alt, onClose }) => {
  useEffect(() => {
    if (!src) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // The page behind a modal must not scroll under it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [src, onClose]);

  if (!src || typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <Backdrop
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Product image"}
    >
      <Frame onClick={(e) => e.stopPropagation()}>
        <Close type="button" onClick={onClose} aria-label="Close">
          &times;
        </Close>
        <ProtectedImage src={optimizedImage(src, "card2x")} alt={alt || ""} />
        {alt ? <div className="cap">{alt}</div> : null}
      </Frame>
    </Backdrop>,
    document.body
  );
};

/**
 * One preview and one modal for a whole list, however many rows it has.
 */
export const useImagePeek = () => {
  const [hover, setHover] = useState(null); // { key, src, alt, rect }
  const [modal, setModal] = useState(null); // { src, alt }
  const timerRef = useRef(null);
  const openRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const close = useCallback(() => {
    clearTimer();
    setHover(null);
  }, []);

  useEffect(() => {
    openRef.current = hover !== null;
  }, [hover]);

  // A fixed panel anchored to a row drifts away from it as soon as anything
  // scrolls, so it goes rather than point at the wrong thing.
  useEffect(() => {
    if (!hover) return undefined;
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [hover, close]);

  useEffect(() => clearTimer, []);

  const open = useCallback((src, alt, target) => {
    if (!src || !target) return;
    const rect = target.getBoundingClientRect();
    clearTimer();
    // Once one is up, moving to the next row swaps instantly; the delay only
    // guards the first, so running the pointer down a list flashes nothing.
    if (openRef.current) {
      setHover({ src, alt, rect });
      return;
    }
    timerRef.current = setTimeout(
      () => setHover({ src, alt, rect }),
      OPEN_DELAY_MS
    );
  }, []);

  const handlers = useCallback(
    (src, alt) => ({
      onMouseEnter: (e) => open(src, alt, e.currentTarget),
      onMouseLeave: close,
      onFocus: (e) => open(src, alt, e.currentTarget),
      onBlur: close,
      onClick: () => {
        close();
        if (src) setModal({ src, alt });
      },
    }),
    [open, close]
  );

  return {
    hover,
    modal,
    handlers,
    closeModal: () => setModal(null),
  };
};

export default useImagePeek;
